// Castagnoli CRC32C — same polynomial GCS uses for object integrity
// hashes and that the data-api emits in storage.change events. Exposed so
// apps can hash their own bytes and compare against an event's crc32c.
//
// The table-driven implementation is the textbook one; it's ~1.5 ns/byte
// in modern JS engines, fast enough for our 10 MiB per-object cap. Kept
// dependency-free to avoid bloating the UMD bundle.

const POLY = 0x82f63b78; // Castagnoli, reflected
const TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) {
      c = (c & 1) ? (POLY ^ (c >>> 1)) : (c >>> 1);
    }
    t[i] = c >>> 0;
  }
  return t;
})();

// crc32cBytes returns the Castagnoli CRC32C of `bytes` as an unsigned 32-bit
// integer.
export function crc32cBytes(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    const byte = bytes[i] as number;
    c = (TABLE[(c ^ byte) & 0xff] as number) ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

// formatCRC32C produces the GCS-canonical base64-of-big-endian-uint32
// string, matching internal/gcs.FormatCRC32C on the server. Apps that
// want to verify their own data integrity can compare a locally-
// computed crc32cBytes(blob)' formatted value against the event's
// `crc32c` field.
export function formatCRC32C(sum: number): string {
  const buf = new Uint8Array(4);
  buf[0] = (sum >>> 24) & 0xff;
  buf[1] = (sum >>> 16) & 0xff;
  buf[2] = (sum >>> 8) & 0xff;
  buf[3] = sum & 0xff;
  let bin = "";
  for (let i = 0; i < buf.length; i++) {
    bin += String.fromCharCode(buf[i] as number);
  }
  return btoa(bin);
}

// crc32cBlob is a convenience wrapper that hashes a Blob's bytes and
// returns the GCS-canonical base64 string — the form an app compares
// against a storage.change event's `crc32c`.
export async function crc32cBlob(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  return formatCRC32C(crc32cBytes(new Uint8Array(buf)));
}
